import {GeoLocation} from '../../../shared/model/geoLocation';
import {GeoLocationService} from '../services/geo-location.service';
import {AdminLocationEditComponent} from './admin-location-edit.component';
import {DatePipe} from '@angular/common';
import {ItalianDatePipe} from '../../../shared/pipes/italian-date.pipe';
import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {Observable} from 'rxjs';
import {PermissionService} from '../../../core/services/permission.service';
import {SystemPower} from '../../../shared/model/permissions';
import {UserService} from '../../../shared/services/user.service';
import {OrganizationService} from '../../../shared/services/organization.service';
import {EventService} from '../../../shared/services/event.service';
import {GameTablesService} from '../services/gametables.service';
import {GameService} from '../services/game.service';
import {AlertService} from '../../../shared/services/alert.service';
import {ConfirmDialogService} from '../../../shared/services/confirm-dialog.service';
import {Messages} from '../../../shared/constants/messages';
import {User} from '../../../shared/model/user';
import {Organization} from '../../../shared/model/organization';
import {Event} from '../../../shared/model/event';
import {GameTable} from '../../../shared/model/game-table';
import {Game} from '../../../shared/model/game';
import {PaginatedListComponent} from '../../../shared/components/paginated-list/paginated-list.component';
import {OrganizationEditComponent} from '../organization/organization-edit/organization-edit.component';
import {EventEditComponent} from '../events/event-edit/event-edit.component';
import {GameTableEditComponent} from '../gametables/game-table-edit/game-table-edit.component';
import {GameEditComponent} from '../games/game-edit/game-edit.component';
import {AdminUserEditComponent} from './admin-user-edit.component';
import {ApiResponse} from '../../../shared/model/api-response';
import {GameCardComponent} from '../games/game-list/game-card/game-card.component';
import {NgbAccordionModule, NgbModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-admin',
  imports: [
    RouterLink,
    ItalianDatePipe,
    NgbAccordionModule,
    PaginatedListComponent,
    GameCardComponent,
  ],
  templateUrl: './admin.html',
  styleUrl: './admin.scss',
})
export class Admin implements OnInit {
  private permissionService = inject(PermissionService);
  private userService = inject(UserService);
  private organizationService = inject(OrganizationService);
  private eventService = inject(EventService);
  private gameTablesService = inject(GameTablesService);
  private geoLocationService = inject(GeoLocationService);
  private gameService = inject(GameService);
  private alertService = inject(AlertService);
  private confirmDialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  protected readonly currentUserId = inject(PermissionService).getCurrentUser()?.id;

  protected readonly users = signal<User[]>([]);
  protected readonly organizations = signal<Organization[]>([]);
  protected readonly events = signal<Event[]>([]);
  protected readonly tables = signal<GameTable[]>([]);
  protected readonly games = signal<Game[]>([]);
  protected readonly locations = signal<GeoLocation[]>([]);

  protected readonly unlinkedGames = computed(() =>
    this.games().filter((g) => !g.bggId),
  );

  ngOnInit(): void {
    this.confirmAdministratorThenLoad();
  }

  private confirmAdministratorThenLoad(): void {
    if (!this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR)) {
      this.alertService.showError(Messages.ADMIN_ONLY);
      this.router.navigate(['/home']);
      return;
    }

    this.userService.getMyUserData().subscribe({
      next: (response) => {
        const userType = response.data?.userType;
        if (!response.success || userType !== 'ADMINISTRATOR') {
          this.alertService.showError(Messages.ADMIN_ONLY);
          this.router.navigate(['/home']);
          return;
        }
        this.loadUsers();
        this.loadOrganizations();
        this.loadEvents();
        this.loadTables();
        this.loadGames();
        this.loadLocations();
      },
      error: () => {
        this.router.navigate(['/home']);
      },
    });
  }

  protected loadUsers(): void {
    this.userService.getAllUsers().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.users.set(response.data);
        }
      },
    });
  }

  protected loadOrganizations(): void {
    this.organizationService.getAllOrganizations(true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.organizations.set(response.data);
        }
      },
    });
  }

  protected loadEvents(): void {
    this.eventService.getAllEvents(true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.events.set(response.data);
        }
      },
      error: () => this.alertService.showError(Messages.EVENT_LOAD_ERROR),
    });
  }

  protected loadTables(): void {
    this.gameTablesService.getAllTables(true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tables.set(response.data);
        }
      },
    });
  }

  protected loadLocations(): void {
    this.geoLocationService.getAllLocations().subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.locations.set(response.data);
        }
      },
    });
  }

  protected loadGames(): void {
    this.gameService.getAllGames().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.games.set(response.data);
        }
      },
    });
  }

  protected onEditGame(game: Game): void {
    if (game?.id) {
      this.openEditor(GameEditComponent, {embeddedId: game.id}, () => this.loadGames());
    }
  }

  protected async onDeleteGame(game: Game): Promise<void> {
    if (!game.id) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_GAME_DELETE_TITLE,
      message: Messages.confirmGameDelete(game.gameName),
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.gameService.deleteGame(game.id).subscribe({
      next: () => this.loadGames(),
    });
  }

  // ---- Utenti ----
  protected onEditUser(user: User): void {
    if (user) {
      this.openEditor(AdminUserEditComponent, {userId: user.id}, () => this.loadUsers());
    }
  }

  protected async onToggleUserActivation(user: User, activate: boolean): Promise<void> {
    if (!user || user.id === this.currentUserId) {
      return;
    }

    const isPending = !!user.userStatus?.emailVerificationPending;
    const title = activate
      ? (isPending ? Messages.CONFIRM_USER_FORCE_ACTIVATION_TITLE : Messages.CONFIRM_USER_ACTIVATION_TITLE)
      : Messages.CONFIRM_USER_DEACTIVATION_TITLE;

    const message = activate
      ? (isPending ? Messages.confirmUserForceActivation(user.userName) : Messages.confirmUserActivation(user.userName))
      : Messages.confirmUserDeactivation(user.userName);

    const confirmLabel = activate ? (isPending ? 'Forza attivazione' : 'Attiva') : 'Disattiva';
    const variant: 'primary' | 'danger' = activate ? 'primary' : 'danger';

    const accepted = await this.confirmDialogService.confirm({
      title,
      message,
      confirmLabel,
      variant,
    });

    if (!accepted) {
      return;
    }

    this.userService.setUserActivation(user.id, activate).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.showSuccess(
            activate ? Messages.USER_ACTIVATION_SUCCESS : Messages.USER_DEACTIVATION_SUCCESS,
          );
          this.loadUsers();
        }
      },
    });
  }

  protected onDeleteUser(user: User): void {
    this.confirmAndDelete(
      Messages.CONFIRM_USER_DELETE_TITLE,
      Messages.confirmUserDelete(user.userName),
      this.userService.deleteUser(user.id),
      Messages.USER_DELETED_SUCCESS,
      () => this.loadUsers(),
    );
  }

  // ---- Organizzazioni ----
  protected onEditOrganization(organization: Organization): void {
    if (organization) {
      this.openEditor(OrganizationEditComponent, {embeddedId: organization.id}, () =>
        this.loadOrganizations(),
      );
    }
  }

  protected onDeleteOrganization(organization: Organization): void {
    this.confirmAndDelete(
      Messages.CONFIRM_ORGANIZATION_DELETE_TITLE,
      Messages.confirmOrganizationDelete(organization.organizationName),
      this.organizationService.deleteOrganization(organization.id),
      Messages.ORGANIZATION_DELETED_SUCCESS,
      () => {
        this.loadOrganizations();
        this.loadEvents();
        this.loadTables();
      },
    );
  }

  // ---- Eventi ----
  protected onEditEvent(event: Event): void {
    if (!event.id) {
      return;
    }
    this.openEditor(EventEditComponent, {embeddedId: event.id}, () => {
      this.loadEvents();
      this.loadTables();
    });
  }

  protected async onToggleEventActivation(event: Event, active: boolean): Promise<void> {
    if (!event.id) {
      return;
    }
    const title = active ? 'Attiva Evento' : 'Disattiva Evento';
    const message = active
      ? `Sei sicuro di voler attivare l'evento "${event.name}"?`
      : `Sei sicuro di voler disattivare l'evento "${event.name}"?`;
    const confirmLabel = active ? 'Attiva' : 'Disattiva';
    const variant: 'primary' | 'danger' = active ? 'primary' : 'danger';

    const accepted = await this.confirmDialogService.confirm({
      title,
      message,
      confirmLabel,
      variant,
    });

    if (!accepted) {
      return;
    }

    const updatedEvent: Event = {
      ...event,
      active,
    };

    this.eventService.updateEvent(updatedEvent).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.showSuccess(
            active ? 'Evento attivato con successo' : 'Evento disattivato con successo',
          );
          this.loadEvents();
          this.loadTables();
        }
      },
    });
  }

  protected onDeleteEvent(event: Event): void {
    if (!event.id) {
      return;
    }
    this.confirmAndDelete(
      Messages.CONFIRM_EVENT_DELETE_TITLE,
      Messages.confirmEventDelete(event.name),
      this.eventService.deleteEvent(event.id),
      Messages.EVENT_DELETE_SUCCESS,
      () => {
        this.loadEvents();
        this.loadTables();
      },
    );
  }

  // ---- Tavoli ----
  protected onEditTable(table: GameTable): void {
    if (table) {
      this.openEditor(GameTableEditComponent, {embeddedId: table.id}, () => this.loadTables());
    }
  }

  protected async onToggleTableActivation(table: GameTable, isActive: boolean): Promise<void> {
    if (!table.id) {
      return;
    }
    const title = isActive ? 'Attiva Tavolo' : 'Disattiva Tavolo';
    const message = isActive
      ? `Sei sicuro di voler attivare il tavolo "${table.tableName}"?`
      : `Sei sicuro di voler disattivare il tavolo "${table.tableName}"?`;
    const confirmLabel = isActive ? 'Attiva' : 'Disattiva';
    const variant: 'primary' | 'danger' = isActive ? 'primary' : 'danger';

    const accepted = await this.confirmDialogService.confirm({
      title,
      message,
      confirmLabel,
      variant,
    });

    if (!accepted) {
      return;
    }

    const updatedTable: Partial<GameTable> = {
      ...table,
      isActive,
    };

    this.gameTablesService.updateTable(updatedTable).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.showSuccess(
            isActive ? 'Tavolo attivato con successo' : 'Tavolo disattivato con successo',
          );
          this.loadTables();
        }
      },
    });
  }

  protected onEditLocation(location: GeoLocation): void {
    if (location) {
      this.openEditor(AdminLocationEditComponent, {location}, () => this.loadLocations());
    }
  }

  protected onDeleteLocation(location: GeoLocation): void {
    if (!location.id) return;
    this.confirmAndDelete(
      "Elimina Luogo",
      `Sei sicuro di voler eliminare il luogo "${location.formalName || location.address}"?`,
      this.geoLocationService.deleteLocation(location.id) as any,
      "Luogo eliminato con successo",
      () => this.loadLocations()
    );
  }

  protected onDeleteTable(table: GameTable): void {
    this.confirmAndDelete(
      Messages.CONFIRM_TABLE_DELETE_TITLE,
      Messages.confirmTableDelete(table.tableName),
      this.gameTablesService.deleteTable(table.id),
      Messages.TABLE_DELETED_SUCCESS,
      () => this.loadTables(),
    );
  }

  /** Apre un componente di edit in modale: salva/annulla restano nella pagina admin. */
  private openEditor<T extends {
    saved: { subscribe: (fn: () => void) => unknown };
    cancelled: { subscribe: (fn: () => void) => unknown }
  }>(
    component: new (...args: never[]) => T,
    inputs: Partial<Record<string, unknown>>,
    onSaved: () => void,
  ): void {
    const modalRef = this.modalService.open(component, {size: 'xl', backdrop: 'static'});
    Object.assign(modalRef.componentInstance, inputs);
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      onSaved();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }

  private async confirmAndDelete(
    title: string,
    message: string,
    request: Observable<ApiResponse<string>>,
    successMessage: string,
    onDone: () => void,
  ): Promise<void> {
    const accepted = await this.confirmDialogService.confirm({
      title,
      message,
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    request.subscribe({
      next: () => {
        this.alertService.showSuccess(successMessage);
        onDone();
      },
    });
  }
}
